/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAgentList.ts (Agents)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Agents list state and logic.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchAgents } from '../api/agentsApi';
import { ListAgentsParams, AgentResponse } from '../types';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';

interface UseAgentListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing Agents list
 * Implements Domain Contextual Filtering pattern
 */
export function useAgentList(options: UseAgentListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const queryParams: ListAgentsParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        domainId: effectiveDomainId
      };

      const result = await fetchAgents(queryParams);

      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('agents.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, effectiveDomainId]);

  const availableColumns = useMemo(() => [
    { key: 'name', label: t('agents.columns.name'), visible: true, sortable: true },
    { key: 'extension', label: t('agents.columns.extension'), visible: true, sortable: true },
    { key: 'status', label: t('agents.columns.status'), visible: true, sortable: true },
    { key: 'enabled', label: t('agents.columns.enabled'), visible: true, sortable: true },
    { key: 'createdAt', label: t('agents.columns.createdAt'), visible: true, sortable: true },
    { key: 'actions', label: t('common.actions'), visible: true, sortable: false }
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'agents',
    reloadKey: effectiveDomainId ?? null
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as AgentResponse[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId
  };
}

